import { CaseData } from './types';

export interface TimelineEntry {
  date: string;        // 标准化日期字符串，用于排序
  display: string;     // 展示用的原始日期文本
  description: string; // 这个日期代表什么
  source: string;      // 来源（文档标题 or "证词"）
  approximate: boolean;// 是否是模糊日期（如"6月初"）
  flagged: boolean;    // 是否与 issues 相关
}

// 从字符串中提取所有日期
function extractFromText(text: string): Array<{ display: string; date: string; approximate: boolean }> {
  const results: Array<{ display: string; date: string; approximate: boolean }> = [];

  // ISO 格式：2023-06-10
  const isoPattern = /\b(\d{4})-(\d{2})-(\d{2})\b/g;
  let m: RegExpExecArray | null;
  while ((m = isoPattern.exec(text)) !== null) {
    results.push({ display: m[0], date: m[0], approximate: false });
  }

  // 中文格式：2023年6月初 / 2023年6月10日 / 2023年6月
  const cnPattern = /(\d{4})年(\d{1,2})月(?:(\d{1,2})日)?(初|上旬|中旬|中|下旬|末)?/g;
  while ((m = cnPattern.exec(text)) !== null) {
    const year = m[1];
    const month = m[2].padStart(2, '0');
    const dayStr = m[3] ? m[3].padStart(2, '0') : null;
    const qualifier = m[4] ?? null;
    const approximate = !dayStr || !!qualifier;

    let day = '01';
    if (dayStr) day = dayStr;
    else if (qualifier === '中旬' || qualifier === '中') day = '15';
    else if (qualifier === '下旬' || qualifier === '末') day = '20';

    results.push({
      display: m[0],
      date: `${year}-${month}-${day}`,
      approximate,
    });
  }

  return results;
}

// 判断某个日期文本是否与 issues 描述相关
function isFlagged(display: string, source: string, issues: CaseData['issues']): boolean {
  return issues.some(issue =>
    issue.description.includes(display) ||
    issue.description.includes(source)
  );
}

export function extractTimeline(caseData: CaseData): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  // 从文档字段中提取
  caseData.documents.forEach(doc => {
    Object.entries(doc.content).forEach(([key, value]) => {
      const found = extractFromText(value);
      found.forEach(({ display, date, approximate }) => {
        entries.push({
          date,
          display,
          description: key,
          source: doc.title,
          approximate,
          flagged: isFlagged(display, key, caseData.issues),
        });
      });
    });
  });

  // 从证词中提取
  caseData.testimony.forEach((para, idx) => {
    const found = extractFromText(para);
    found.forEach(({ display, date, approximate }) => {
      entries.push({
        date,
        display,
        description: para.length > 30 ? para.slice(0, 30) + '…' : para,
        source: `证词第${idx + 1}段`,
        approximate,
        flagged: isFlagged(display, para, caseData.issues),
      });
    });
  });

  // 去重（相同 date + source + description）
  const seen = new Set<string>();
  const unique = entries.filter(e => {
    const key = `${e.date}|${e.source}|${e.description}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // 按日期排序
  return unique.sort((a, b) => a.date.localeCompare(b.date));
}
